import {
  AdjustmentsHorizontalIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  PlusIcon,
  TrashIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import {
  v2StrategiesList,
  v2StrategiesCreate,
  v2StrategiesDelete,
  v2StrategiesClone,
  v2StrategiesMarketplace,
} from "../services/tradingAgentsV2";

export default function Strategies() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"mine" | "marketplace">("mine");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [debateRounds, setDebateRounds] = useState("3");
  const [isPublic, setIsPublic] = useState(false);

  const myQuery = useQuery({
    queryKey: ["strategies-mine"],
    queryFn: v2StrategiesList,
    enabled: tab === "mine",
  });

  const marketQuery = useQuery({
    queryKey: ["strategies-marketplace"],
    queryFn: () => v2StrategiesMarketplace({ sort: "accuracy", limit: 50 }),
    enabled: tab === "marketplace",
  });

  const createMutation = useMutation({
    mutationFn: v2StrategiesCreate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["strategies-mine"] });
      setName("");
      setDescription("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: v2StrategiesDelete,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["strategies-mine"] }),
  });

  const cloneMutation = useMutation({
    mutationFn: v2StrategiesClone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["strategies-mine"] });
      queryClient.invalidateQueries({ queryKey: ["strategies-marketplace"] });
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
      config: {
        max_debate_rounds: Number(debateRounds),
      },
      is_public: isPublic,
    });
  };

  const strategies =
    tab === "mine"
      ? myQuery.data?.strategies ?? []
      : marketQuery.data?.strategies ?? [];

  return (
    <div className="space-y-6">
      {/* Tab selector */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        <button
          type="button"
          onClick={() => setTab("mine")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${
            tab === "mine"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <AdjustmentsHorizontalIcon className="mr-1.5 inline h-4 w-4" />
          My Strategies
        </button>
        <button
          type="button"
          onClick={() => setTab("marketplace")}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-all ${
            tab === "marketplace"
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <GlobeAltIcon className="mr-1.5 inline h-4 w-4" />
          Marketplace
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {tab === "mine" && (
          <div className="card lg:col-span-1">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">
              New Strategy
            </h2>
            <form onSubmit={handleCreate} className="space-y-3">
              <Input
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Aggressive Bull"
                required
              />
              <Input
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Higher debate rounds, risk-on bias"
              />
              <Input
                label="Debate Rounds"
                type="number"
                min="1"
                max="10"
                value={debateRounds}
                onChange={(e) => setDebateRounds(e.target.value)}
              />
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Publish to marketplace
              </label>
              {createMutation.isError && (
                <p className="text-sm text-red-600">
                  {(createMutation.error as Error).message}
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                isLoading={createMutation.isPending}
                leftIcon={<PlusIcon className="h-5 w-5" />}
              >
                Create Strategy
              </Button>
            </form>
          </div>
        )}

        <div className={tab === "mine" ? "lg:col-span-2" : "lg:col-span-3"}>
          {strategies.length === 0 ? (
            <div className="card">
              <p className="text-sm text-gray-500">
                {tab === "mine"
                  ? "No strategies yet. Create one to customize the agent pipeline."
                  : "No public strategies in the marketplace yet."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {strategies.map((s) => (
                <div key={s.id} className="card border border-gray-200">
                  <div className="mb-2 flex items-start justify-between">
                    <h3 className="text-base font-semibold text-gray-900">
                      {s.name}
                    </h3>
                    <div className="flex gap-1">
                      {tab === "marketplace" && (
                        <button
                          type="button"
                          onClick={() => cloneMutation.mutate(s.id)}
                          className="rounded p-1 text-gray-400 hover:bg-primary-50 hover:text-primary-600"
                          title="Clone"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      )}
                      {tab === "mine" && (
                        <button
                          type="button"
                          onClick={() => deleteMutation.mutate(s.id)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {s.description && (
                    <p className="mb-2 text-sm text-gray-600">{s.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {s.backtest_accuracy != null && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-yellow-50 px-2 py-0.5 text-yellow-700">
                        <TrophyIcon className="h-3 w-3" />
                        {s.backtest_accuracy.toFixed(1)}% accuracy
                      </span>
                    )}
                    <span className="rounded bg-gray-100 px-2 py-0.5">
                      {s.clone_count} clones
                    </span>
                    {s.is_public && (
                      <span className="rounded bg-blue-50 px-2 py-0.5 text-blue-600">
                        Public
                      </span>
                    )}
                    {s.config?.max_debate_rounds != null && (
                      <span className="rounded bg-gray-100 px-2 py-0.5">
                        {String(s.config.max_debate_rounds)} rounds
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
